import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Circle, Icon, Stack } from '@chakra-ui/react';
import { BsSearch } from 'react-icons/bs';

import {
	Modal,
	PaginatedItemGrid,
	ItemSearchFiltersForm,
} from 'components';
import { ItemModel } from 'models';
import { ItemSearchFiltersFormData } from 'components/Form/ItemSearchFiltersForm/ItemSearchFiltersForm';
import { NavigationService } from 'services';
import { extractFiltersFromLocation, updateSearchFiltersInUrl } from 'utils/search';
import { filterEmptyValues } from 'utils/object';
import { QuerySearchParameters } from 'constants/url';
import { useItemSearch } from 'hooks/queries/useSearch';
import { useArtists } from 'hooks/queries/useArtists';
import { useMaterials } from 'hooks/queries/useMaterials';

export default function ItemOverviewView() {
	const location = useLocation();
	const navigate = useNavigate();

	const { item: defaultItemFilters } = extractFiltersFromLocation(location);

	const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
	const [itemSearchFilters, setItemSearchFilters] = useState<ItemSearchFiltersFormData | undefined>(defaultItemFilters);
	const [page, setPage] = useState(1);

	const { data: itemSearchResults, isLoading: itemSearchLoading } = useItemSearch(itemSearchFilters, page);
	const { data: artistList = [] } = useArtists();
	const { data: materialList = [] } = useMaterials();

	useEffect(() => {
		const { item } = extractFiltersFromLocation(location);
		setItemSearchFilters(item);
	}, [location]);

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const pageParam = Number(queryParams.get(QuerySearchParameters.PAGE) || '1');
		setPage(Number.isNaN(pageParam) ? 1 : pageParam);
	}, [location.search]);

	const openFiltersModal = () => setIsFiltersModalOpen(true);
	const closeFiltersModal = () => setIsFiltersModalOpen(false);

	const handleItemSearchSubmit = (searchQuery: ItemSearchFiltersFormData) => {
		const cleanedFilters = filterEmptyValues(searchQuery) as ItemSearchFiltersFormData;
		setItemSearchFilters(cleanedFilters);
		setPage(1);
		updateSearchFiltersInUrl({
			location,
			navigate,
			filters: cleanedFilters,
			filterParamKey: QuerySearchParameters.ITEM_FILTERS,
			paramMappings: [
				{ key: QuerySearchParameters.SORT_BY, value: cleanedFilters.sortBy },
				{ key: QuerySearchParameters.SORT_TYPE, value: cleanedFilters.sortType },
				{ key: QuerySearchParameters.TYPE, value: cleanedFilters.type },
			],
		});
		closeFiltersModal();
	};

	const updatePageInURL = (pageNumber: number) => {
		const queryParams = new URLSearchParams(location.search);
		queryParams.set(QuerySearchParameters.PAGE, pageNumber.toString());
		navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
	};

	const handleItemsPageSelect = (pageNumber: number) => {
		setPage(pageNumber);
		updatePageInURL(pageNumber);
	};

	const handleItemClick = (item: ItemModel) => {
		navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName()));
	};

	return (
		<Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
			<Stack>
			<PaginatedItemGrid
				onItemCardClick={handleItemClick}
				onItemNameClick={handleItemClick}
				paginatedItems={itemSearchResults}
				onPageSelect={handleItemsPageSelect}
				scrollMode={false}
				gridMaxHeight={1200}
				isLoading={itemSearchLoading}
				showMetadata={true}
				defaultMetadataExpanded={true}
			/>
				<Circle
					size="40px"
					bg="gray.300"
					left="50%"
					transform="translateX(-50%)"
					position="fixed"
					bottom={100}
					cursor="pointer"
					onClick={openFiltersModal}
					zIndex="overlay"
				>
					<Icon as={BsSearch} color="white.100" />
				</Circle>
			</Stack>

			<Modal
				isOpen={isFiltersModalOpen}
				onClose={closeFiltersModal}
				showSubmitButton={false}
				showCloseButton={false}
				title="Item Search Filters"
				size="6xl"
				content={
					<ItemSearchFiltersForm
						artistList={artistList}
						materialList={materialList}
						defaultFormData={itemSearchFilters}
						onSubmit={handleItemSearchSubmit}
						submitLoading={itemSearchLoading}
					/>
				}
			/>
		</Box>
	);
}
